import { format } from "date-fns";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle, Clock } from "lucide-react";

interface Payment {
  month: number;
  dueDate: Date;
  totalAmount: number;
  companyAmount: number;
  creditorAmount: number;
  isFirstPeriod: boolean;
}

interface PaymentScheduleProps {
  payments: Payment[];
  amountPaid: number;
}

export const PaymentSchedule = ({ payments, amountPaid }: PaymentScheduleProps) => {
  let remainingAmountPaid = amountPaid;
  const today = new Date();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-white">Monthly Payment Schedule</h4>
        <div className="flex gap-2">
          <Badge variant="outline" className="bg-green-900/20 text-green-400 border-green-500/20">
            <CheckCircle className="w-4 h-4 mr-1" /> Paid
          </Badge>
          <Badge variant="outline" className="bg-red-900/20 text-red-400 border-red-500/20">
            <AlertTriangle className="w-4 h-4 mr-1" /> Overdue
          </Badge>
          <Badge variant="outline" className="bg-blue-900/20 text-blue-400 border-blue-500/20">
            <Clock className="w-4 h-4 mr-1" /> Upcoming
          </Badge>
        </div>
      </div>

      <div className="space-y-3">
        {payments.map((payment, index) => {
          const isPaid = remainingAmountPaid >= payment.totalAmount;
          const partiallyPaid = !isPaid && remainingAmountPaid > 0;
          
          if (isPaid) {
            remainingAmountPaid -= payment.totalAmount;
          } else if (partiallyPaid) {
            const partialAmount = remainingAmountPaid;
            remainingAmountPaid = 0;
          }

          const isOverdue = !isPaid && payment.dueDate < today;
          const daysUntilDue = Math.ceil((payment.dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          const isUpcoming = !isPaid && !isOverdue && daysUntilDue <= 7;

          return (
            <Card 
              key={index}
              className={`p-4 ${
                isPaid 
                  ? 'bg-green-900/20 border border-green-500/20' 
                  : isOverdue
                  ? 'bg-red-900/20 border border-red-500/20'
                  : 'bg-blue-900/20 border border-blue-500/20'
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <h5 className="text-white font-medium">Payment {payment.month}</h5>
                    <Badge 
                      variant={isPaid ? "secondary" : isOverdue ? "destructive" : "outline"}
                      className={`${
                        partiallyPaid 
                          ? "bg-yellow-900/20 text-yellow-400" 
                          : !isPaid && !isOverdue 
                          ? "bg-blue-900/20 text-blue-400 border-blue-500/20" 
                          : ""
                      }`}
                    >
                      {isPaid ? 'Paid' : isOverdue ? 'Overdue' : isUpcoming ? 'Due Soon' : 'Upcoming'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-400 mt-1">
                    Due: {format(payment.dueDate, 'dd MMMM yyyy')}
                    {!isPaid && (
                      isOverdue 
                        ? ` (${Math.abs(daysUntilDue)} days overdue)`
                        : daysUntilDue <= 7 
                        ? ` (${daysUntilDue} days until due)`
                        : ''
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-white font-medium">R{payment.totalAmount.toFixed(2)}</p>
                  {partiallyPaid && (
                    <p className="text-yellow-400 text-sm">
                      Partially Paid: R{remainingAmountPaid.toFixed(2)}
                    </p>
                  )}
                  <div className="text-sm text-gray-400 mt-1">
                    {payment.isFirstPeriod ? (
                      <p>100% to company</p>
                    ) : (
                      <>
                        <p>Company (10%): R{payment.companyAmount.toFixed(2)}</p>
                        <p>Creditors (90%): R{payment.creditorAmount.toFixed(2)}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};